/**
 * @author Hiroki Moto
 */

import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import equals from 'ramda/src/equals';
import isEmpty from 'ramda/src/isEmpty';
import remove from 'ramda/src/remove';
import toLower from 'ramda/src/toLower';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { toast } from 'react-toastify';
import Accordion from '@components/@core/Accordion';
import Button from '@components/@core/AnimatedButton';
import Dropdown from '@components/@core/Dropdown';
import Filter from '@components/@core/Filter';
import Input from '@components/@core/Input';
import HabitListItem from './HabitListItem';

const INCLUDED = ['Daily', 'Weekly', 'Monthly'];

const Add = ({ authors = [], departments = [], competencies = [], categories = [], onCreate = () => {}, onModal = () => {} }) => {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [selected, setSelected] = useState({
        author: [],
        department: [],
        competency: [],
        category: [],
    });
    const [habits, setHabits] = useState([]);
    const [included, setIncluded] = useState({ daily: [], weekly: [], monthly: [] });
    const modalData = useSelector((state) => state.app.modalData);
    const disabled = !title || !description;

    const handleAddHabits = () => {
        onModal({
            type: 'Attach Library',
            data: { before: { show: ['habits'], modules: [] }, after: habits },
            callBack: {
                onAttach: (e) => {
                    const _habits = habits.concat(e.templates);
                    setHabits(_habits);

                    const daily = [];
                    const weekly = [];
                    const monthly = [];
                    _habits.map((item) => {
                        const { schedule_interval } = item.data;
                        if (schedule_interval === 'day') daily.push(item);
                        else if (schedule_interval === 'week') weekly.push(item);
                        else monthly.push(item);
                        setIncluded({ daily, weekly, monthly });
                    });
                },
                onAdd: () => onModal({ type: 'Add New Habit', data: { after: { habits } } }),
            },
        });
    };

    const handleSelectTags = (key) => (tags) => {
        if (!equals(selected[key], tags)) {
            selected[key] = tags;
            setSelected(selected);
        }
    };

    const handleHabitList = (event, section, index) => {
        if (event === 'remove') {
            const _habits = remove(index, 1, included[section]);
            included[section] = _habits;
            setIncluded({ ...included });
        }
    };

    const handleSubmit = (published) => () => {
        if (isEmpty(title) || isEmpty(selected.author) || isEmpty(habits)) {
            toast.error(`Please enter Title Author and Habit before ${published === 0 ? `Saving Draft` : `Publishing the Schedule`}`, {
                position: toast.POSITION.TOP_RIGHT,
            });
            return;
        }

        const _habits = habits.map((e) => ({
            blocked_by: null,
            card_template_id: e.id,
            card_type_id: 17,
            complete_course: 0,
            delay_days: 0,
        }));

        const data = {
            template: {
                card_type_id: 17,
                name: title,
                author_id: selected.author[0],
                access_type: 1,
                children: habits,
                published,
                data: {
                    description,
                    modules: _habits,
                    author: selected.author,
                    category: selected.category,
                    competency: selected.competency,
                    department: selected.department,
                    type: 'recurring',
                },
            },
        };

        const payload = { type: 'habitslist', data };
        onCreate(payload);
    };

    useEffect(() => {
        if (modalData?.after?.habits) {
            const _habits = modalData.after.habits;
            setHabits(_habits);
            const daily = [];
            const weekly = [];
            const monthly = [];
            _habits.map((item) => {
                const { schedule_interval } = item.data;
                if (schedule_interval === 'day') daily.push(item);
                else if (schedule_interval === 'week') weekly.push(item);
                else monthly.push(item);
                setIncluded({ daily, weekly, monthly });
            });
        }
    }, [modalData]);

    return (
        <>
            <Filter back="all habit schedules" />
            <div className="lib-todo-habit-schedule">
                <div className="habit-schedule-detail">
                    <div className="d-flex py-2">
                        <p className="dsl-b18 bold mb-0">Add Habit Schedule</p>
                    </div>
                    <Input className="input-field" title="Title" type="text" direction="vertical" value={title} placeholder="Type here..." onChange={(e) => setTitle(e)} />
                    <Input
                        title="Description"
                        className="input-field input-textarea"
                        type="text"
                        as="textarea"
                        rows="2"
                        direction="vertical"
                        value={description}
                        placeholder="Type here..."
                        onChange={(e) => setDescription(e)}
                    />
                    <Accordion className="settings-habit-schedule" icon="fas fa-gear" expanded>
                        <Row className="mt-1">
                            <Col xs={12} sm={6} className="my-3">
                                <Dropdown
                                    className="dropdown-field author"
                                    title="Author"
                                    direction="vertical"
                                    width="fit-content"
                                    data={authors}
                                    getValue={(e) => e.name}
                                    onChange={handleSelectTags('author')}
                                />
                            </Col>
                            <Col xs={12} sm={6} className="my-3">
                                <Dropdown
                                    multi
                                    className="dropdown-field"
                                    title="Categories"
                                    direction="vertical"
                                    width="fit-content"
                                    data={categories}
                                    getValue={(e) => e.name}
                                    placeholder="Select multiple"
                                    onChange={handleSelectTags('category')}
                                />
                            </Col>
                            <Col xs={12} sm={6} className="my-3">
                                <Dropdown
                                    multi
                                    className="dropdown-field"
                                    title="Competencies"
                                    direction="vertical"
                                    width="fit-content"
                                    data={competencies}
                                    getValue={(e) => e.name}
                                    placeholder="Select multiple"
                                    onChange={handleSelectTags('competency')}
                                />
                            </Col>
                            <Col xs={12} sm={6} className="my-3">
                                <Dropdown
                                    multi
                                    className="dropdown-field department"
                                    title="Departments"
                                    direction="vertical"
                                    width="fit-content"
                                    data={departments}
                                    getValue={(e) => e.name}
                                    placeholder="Select multiple"
                                    onChange={handleSelectTags('department')}
                                />
                            </Col>
                        </Row>
                    </Accordion>
                </div>
                <div className="detail-list mt-3">
                    <div className="d-h-between">
                        <p className="dsl-b22 bold pt-3">Habits Included</p>
                        <div className="jc-end d-flex px-3">
                            <Button className="px-0" name="+ Attach Habit" type="link" onClick={handleAddHabits} />
                        </div>
                    </div>
                    {habits.length > 0 ? (
                        INCLUDED.map((item) => {
                            const key = toLower(item);
                            return (
                                <>
                                    <p className="dsl-b18 text-500">{item} Habits</p>
                                    <div className="mb-4">
                                        {included[key].map((habit, index) => (
                                            <HabitListItem
                                                key={`${item}${habit.id}`}
                                                name={habit.name}
                                                description={habit.data.description}
                                                editable
                                                onSelect={(e) => handleHabitList(e, key, index)}
                                            />
                                        ))}
                                    </div>
                                </>
                            );
                        })
                    ) : (
                        <p className="dsl-m12 mb-0">No habits added yet</p>
                    )}
                    <div className="mt-2 d-flex jc-end px-3">
                        <Button className="btn-save me-3" name="Draft" disabled={disabled} type="low" onClick={handleSubmit(0)} />
                        <Button className="btn-save" name="Publish" disabled={disabled} onClick={handleSubmit(1)} />
                    </div>
                </div>
            </div>
        </>
    );
};

export default Add;
